import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { type ProjectComment, type Stored } from "@overmap-ai/common"
import { selectCurrentUser } from "@overmap-ai/core"
import { memo, type ReactElement, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"

interface ProjectCommentDropdownMenuProps
	extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	trigger: ReactElement
	projectComment: Stored<ProjectComment>
	onResolvedChange: (projectComment: Stored<ProjectComment>, resolved: boolean) => void
	onEdit: () => void
	onDelete: (projectComment: Stored<ProjectComment>) => void
}

export const ProjectCommentDropdownMenu = memo((props: ProjectCommentDropdownMenuProps) => {
	const {
		trigger,
		disabled,
		projectComment,
		onResolvedChange,
		onEdit,
		onDelete,
		size,
		radius,
		variant,
		accentColor,
	} = props
	const currentUser = useAppSelector(selectCurrentUser)

	const handleResolvedChange = useCallback(() => {
		onResolvedChange(projectComment, !projectComment.resolved)
	}, [projectComment, onResolvedChange])

	const handleDelete = useCallback(() => {
		onDelete(projectComment)
	}, [projectComment, onDelete])

	return (
		<Menu.Root>
			<Menu.ClickTrigger disabled={disabled}>{trigger}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.Group>
					<Menu.Item onSelect={handleResolvedChange}>
						<LuIcon icon={projectComment.resolved ? modelIconMapping.refresh : modelIconMapping.check} />
						{projectComment.resolved ? "Unresolve" : "Resolve"}
					</Menu.Item>
					{currentUser?.id === projectComment.created_by && (
						<>
							<Menu.Item onSelect={onEdit}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</>
					)}
				</Menu.Group>
			</Menu.Content>
		</Menu.Root>
	)
})
ProjectCommentDropdownMenu.displayName = "ProjectCommentDropdownMenu"
